import React, { useState } from 'react';
import styles from './Filters.module.scss';
import { Minus, Plus } from 'iconoir-react';
import { Button, Card, Checkbox } from '@orderly/morrisons-component-library';
import { IApiRequest, ICategory, IShoppingList, ISubCategoryWithProductCount, ITradingPack } from '../../../../types';
import { mapCategory } from '../../../../helpers/Category/mapCategory';
import { SkeletonLoader } from '../../../../components';

interface IFilterSectionProps {
  title: string;
  children: React.ReactNode;
  startOpen?: boolean;
}

const FilterSection: React.FC<IFilterSectionProps> = ({
  title,
  children,
  startOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(startOpen);

  return (
    <Card className={styles.filterSection}>
      <button
        className={styles.filterHeader}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        {isOpen ? <Minus /> : <Plus />}
      </button>
      {isOpen && <div className={styles.filterContent}>{children}</div>}
    </Card>
  );
};

interface IFiltersProps {
  onCategoryChange: (id: string, checked: boolean) => void;
  onSubCategoryChange: (id: string, checked: boolean) => void;
  onCaseSizeChange: (id: string, checked: boolean) => void;
  onTradingGroupChange: (id: string, checked: boolean) => void;
  onTradingPackCategoryChecked: (id: number, checked: boolean) => void;
  onTradingPackLocationChecked: (id: number, checked: boolean) => void;
  onTradingPromotionChecked: () => void;
  onUseCurrentShoppingList: () => void;
  onTradingPackChecked: () => void;
  selectedCategory: number;
  categories: IApiRequest<ICategory[]>;
  selectedCaseSize: string;
  subCategoriesLoading: boolean;
  subCategories: ISubCategoryWithProductCount[];
  checkedSubCategories: number[];
  useCurrentShoppingList: boolean;
  shoppingList: IApiRequest<IShoppingList>;
  tradingPromotionChecked: boolean;
  tradingGroupsLoading: boolean;
  tradingGroups: string[];
  checkedTradingGroups: string[];
  currentTradingPack: IApiRequest<ITradingPack>;
  tradingPackChecked: boolean;
  tradingPackCheckedCategories: number[];
  tradingPackCheckedLocations: number[];
  clearCheckedSubCategories: () => void;
  clearCheckedTradingGroups: () => void;
  clearTradingPackCategories: () => void;
  clearTradingPackLocations: () => void;
}

const Filters: React.FC<IFiltersProps> = ({
  onCategoryChange,
  onSubCategoryChange,
  onCaseSizeChange,
  onUseCurrentShoppingList,
  onTradingPromotionChecked,
  onTradingGroupChange,
  selectedCategory,
  categories,
  selectedCaseSize,
  subCategoriesLoading,
  subCategories,
  checkedSubCategories,
  useCurrentShoppingList,
  shoppingList,
  tradingPromotionChecked,
  tradingGroupsLoading,
  tradingGroups,
  checkedTradingGroups,
  currentTradingPack,
  tradingPackChecked,
  onTradingPackChecked,
  tradingPackCheckedCategories,
  tradingPackCheckedLocations,
  onTradingPackCategoryChecked,
  onTradingPackLocationChecked,
  clearCheckedSubCategories,
  clearCheckedTradingGroups,
  clearTradingPackCategories,
  clearTradingPackLocations,
}) => {
  return (
    <div className={styles.filtersContainer}>
      <FilterSection title="Filters" startOpen={true}>
        <Checkbox
          checked={tradingPromotionChecked}
          label="Wholesale Promotions"
          name="tradingPromotion"
          onChange={onTradingPromotionChecked}
          isSmall={true}
          className={styles.checkbox}
        />
        {
          shoppingList.data.id !== -1 &&
          shoppingList.data.description.length > 0 &&
          <Checkbox
            checked={useCurrentShoppingList}
            label={`Favourites (${shoppingList.data.description})`}
            name="useShoppingList"
            onChange={onUseCurrentShoppingList}
            isSmall={true}
            className={styles.checkbox}
          />
        }
        {
          currentTradingPack &&
          currentTradingPack.data &&
          <Checkbox
            checked={tradingPackChecked}
            label={currentTradingPack.data.description}
            name="tradingPack"
            onChange={onTradingPackChecked}
            isSmall={true}
            className={styles.checkbox}
          />
        }
      </FilterSection>
      {
        categories.data &&
        <FilterSection title="Category" startOpen={true}>
          <div className={styles.scrollable}>
            {
              categories.loading &&
              <SkeletonLoader />
            }
            {
              categories.data.map(category => (
                <Checkbox
                  checked={selectedCategory === category.id}
                  key={category.id}
                  label={mapCategory(category.description)}
                  name={`cat_${category.id}`}
                  onChange={onCategoryChange}
                  isSmall={true}
                  className={styles.checkbox}
                />
              ))
            }
          </div>
        </FilterSection>
      }
      {
        (subCategoriesLoading || (subCategories && subCategories.length > 0)) &&
        <FilterSection
          title={`Sub Category (${checkedSubCategories.length})`}
          startOpen={true}
        >
          {
            checkedSubCategories.length > 0 &&
            <Button
              type="button"
              className={`${styles.clearFilters} link`}
              text="Clear"
              onClick={() => clearCheckedSubCategories()}
            />
          }
          <div className={styles.scrollable}>
            {
              subCategoriesLoading &&
              <SkeletonLoader />
            }
            {
              subCategories &&
              subCategories.map(subCategory => (
                <Checkbox
                  checked={checkedSubCategories.includes(subCategory.id)}
                  key={subCategory.id}
                  label={`${subCategory.description} (${subCategory.productCount})`}
                  name={`sub_${subCategory.id}`}
                  onChange={onSubCategoryChange}
                  isSmall={true}
                  className={styles.checkbox}
                />
              ))
            }
          </div>
        </FilterSection>
      }
      {
        (tradingGroupsLoading || (tradingGroups && tradingGroups.length > 0)) &&
        <FilterSection
          title={`Trading Groups (${checkedTradingGroups.length})`}
          startOpen={false}
        >
          {
            checkedTradingGroups.length > 0 &&
            <Button
              type="button"
              className={`${styles.clearFilters} link`}
              text="Clear"
              onClick={() => clearCheckedTradingGroups()}
            />
          }
          <div className={styles.scrollable}>
            {
              tradingGroupsLoading &&
              <SkeletonLoader />
            }
            {
              tradingGroups &&
              tradingGroups.map(tradingGroup => (
                <Checkbox
                  checked={checkedTradingGroups.includes(tradingGroup)}
                  key={tradingGroup}
                  label={tradingGroup}
                  name={tradingGroup}
                  onChange={onTradingGroupChange}
                  isSmall={true}
                  className={styles.checkbox}
                />
              ))
            }
          </div>
        </FilterSection>
      }
      {
        (
          tradingPackChecked &&
          currentTradingPack.data.categories &&
          currentTradingPack.data.categories.length > 0
        ) &&
        <FilterSection title="Trading Pack Category">
          {
            checkedTradingGroups.length > 0 &&
            <Button
              type="button"
              className={`${styles.clearFilters} link`}
              text="Clear"
              onClick={() => clearTradingPackCategories()}
            />
          }
          <div className={styles.scrollable}>
            {
              currentTradingPack.data.categories.map(x =>
                <Checkbox
                  checked={tradingPackCheckedCategories.includes(x.id)}
                  key={`trading_pack_category_${x.id}`}
                  label={x.description}
                  name={`trading_pack_category_${x.id}`}
                  onChange={(id, checked) => onTradingPackCategoryChecked(x.id, checked)}
                  isSmall={true}
                  className={styles.checkbox}
                />,
              )
            }
          </div>
        </FilterSection>
      }
      {
        (
          tradingPackChecked &&
          currentTradingPack.data.locations &&
          currentTradingPack.data.locations.length > 0
        ) &&
        <FilterSection title="Trading Pack Location">
          {
            checkedTradingGroups.length > 0 &&
            <Button
              type="button"
              className={`${styles.clearFilters} link`}
              text="Clear"
              onClick={() => clearTradingPackLocations()}
            />
          }
          <div className={styles.scrollable}>
            {
              currentTradingPack.data.locations.map(x =>
                <Checkbox
                  checked={tradingPackCheckedLocations.includes(x.id)}
                  key={`trading_pack_location_${x.id}`}
                  label={x.description}
                  name={`trading_pack_location_${x.id}`}
                  onChange={(id, checked) => onTradingPackLocationChecked(x.id, checked)}
                  isSmall={true}
                  className={styles.checkbox}
                />,
              )
            }
          </div>
        </FilterSection>
      }
      <FilterSection title="Case Size">
        <div className={styles.scrollable}>
          <Checkbox
            checked={selectedCaseSize === '1-7'}
            label="1-7"
            name="1-7"
            onChange={onCaseSizeChange}
            isSmall={true}
            className={styles.checkbox}
          />
          <Checkbox
            checked={selectedCaseSize === '8-20'}
            label="8-20"
            name="8-20"
            onChange={onCaseSizeChange}
            isSmall={true}
            className={styles.checkbox}
          />
          <Checkbox
            checked={selectedCaseSize === '21+'}
            label="21+"
            name="21+"
            onChange={onCaseSizeChange}
            isSmall={true}
            className={styles.checkbox}
          />
        </div>
      </FilterSection>
    </div>
  );
};

export default Filters;
